import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { MailWarning, RefreshCcw, Send, Paperclip } from "lucide-react";

const KIND_LABEL = {
  bienvenue: "Bienvenue",
  prospect_notification: "Notification simulateur",
  email_log: "Courriel",
};

function Row({ item, onResend, busy }) {
  return (
    <Card data-testid={`courriel-row-${item.id}`}>
      <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700" data-testid={`courriel-kind-${item.id}`}>
              {KIND_LABEL[item.kind] || item.kind}
            </span>
            <span className="font-medium text-sm truncate">{item.objet}</span>
            {item.attach_type && (
              <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                <Paperclip className="h-3 w-3" />{item.attach_type}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            À : {item.destinataire || "—"}{item.nom ? ` · ${item.nom}` : ""} · {item.date ? new Date(item.date).toLocaleString("fr-FR") : "—"} · {item.tentatives} tentative{item.tentatives > 1 ? "s" : ""}
          </div>
          {item.erreur && <div className="text-xs text-red-600 mt-1 break-words" data-testid={`courriel-error-${item.id}`}>{item.erreur}</div>}
        </div>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onResend(item)} data-testid={`courriel-resend-${item.id}`}>
          <RefreshCcw className={`h-3 w-3 mr-1 ${busy ? "animate-spin" : ""}`} />Renvoyer
        </Button>
      </CardContent>
    </Card>
  );
}

export default function CourrielsAdmin() {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/courriels/echecs"); setItems(data); window.dispatchEvent(new Event("courriels-updated")); }
    catch { toast.error("Impossible de charger les courriels"); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resend = async (item) => {
    setBusyId(item.id);
    try {
      await api.post("/courriels/renvoyer", { kind: item.kind, id: item.id });
      toast.success(`Courriel renvoyé à ${item.destinataire || "son destinataire"}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Renvoi impossible"); }
    finally { setBusyId(null); }
  };

  const resendAll = async () => {
    setConfirmAll(false);
    try {
      const { data } = await api.post("/courriels/tout-renvoyer");
      toast.success(`${data.reussis}/${data.traites} courriels renvoyés`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Renvoi groupé impossible"); }
  };

  return (
    <div className="space-y-6" data-testid="courriels-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#1E293B]">Courriels non partis</h1>
          <p className="text-slate-500 mt-1">Envois en échec : bienvenue, notifications simulateur, attestations et courriels aux entreprises.</p>
        </div>
        {items?.length > 0 && (
          <Button onClick={() => setConfirmAll(true)} data-testid="courriels-resend-all" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
            <Send className="h-4 w-4 mr-2" />Tout renvoyer ({Math.min(items.length, 50)})
          </Button>
        )}
      </div>

      {items === null ? (
        <p className="text-sm text-slate-500">Chargement…</p>
      ) : items.length === 0 ? (
        <Card data-testid="courriels-empty"><CardContent className="p-6 text-sm text-slate-500 flex items-center gap-2">
          <MailWarning className="h-4 w-4" />Aucun courriel en échec. Tous les envois sont partis.
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="courriels-list">
          {items.map((i) => <Row key={`${i.kind}-${i.id}`} item={i} onResend={resend} busy={busyId === i.id} />)}
        </div>
      )}

      <p className="text-xs text-slate-400">
        Les pièces jointes (attestations, appels) sont régénérées à jour au moment du renvoi. Historique complet : <Link to="/app/emails" className="underline">Emails</Link>.
      </p>

      <Dialog open={confirmAll} onOpenChange={setConfirmAll}>
        <DialogContent data-testid="courriels-confirm-all">
          <DialogHeader>
            <DialogTitle>Tout renvoyer ?</DialogTitle>
            <DialogDescription>
              Jusqu'à 50 courriels en échec seront renvoyés maintenant (pièces jointes régénérées à jour). Un nouveau compte-rendu apparaîtra ensuite.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAll(false)} data-testid="courriels-cancel-all">Annuler</Button>
            <Button onClick={resendAll} data-testid="courriels-confirm-all-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Renvoyer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
